import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Calendar, Phone, Star, Shield, Truck, CheckCircle, 
  ArrowRight, Sparkles, Droplet, Wand2, MapPin, Users
} from 'lucide-react';
import { HERO_DATA, SERVICES_DATA, REVIEWS_DATA } from '../data/mockData';
import SEO from '../components/SEO';

const Home = () => {
  // Paint-Safe Proof Points
  const proofPoints = [
    'Deionized Water',
    'Brand-New Exterior Microfiber Towels',
    'Forced-Air Drying',
    'Owner-Operated Care',
    'Mobile Service Available'
  ];

  // Owner-Operated Differentiators
  const differentiators = [
    {
      id: 'fresh-towels',
      icon: Sparkles,
      title: 'Fresh Exterior Towels',
      description: 'Brand-new microfiber towels are used on painted exterior surfaces to reduce the risk of transferring trapped debris back onto your finish.'
    },
    {
      id: 'deionized-water',
      icon: Droplet,
      title: 'Deionized Water',
      description: 'Spot-reducing rinse water helps leave a cleaner finish, especially in Southern California heat.'
    },
    {
      id: 'forced-air',
      icon: Wand2,
      title: 'Forced-Air Drying',
      description: 'Air moves water out of mirrors, grilles, trim, wheels, and tight areas while reducing unnecessary paint contact.'
    },
    {
      id: 'owner-operated',
      icon: Users,
      title: 'Owner-Operated Service',
      description: 'The person you speak with is the person responsible for the work and the final inspection.'
    }
  ];

  // Signature Services (Top 4)
  const signatureServices = [
    {
      id: 'ceramic-coating',
      title: 'Ceramic Coating',
      description: 'Long-term gloss and easier maintenance begin with proper preparation. Your vehicle is washed, decontaminated, assessed, and corrected as needed before the coating is applied.',
      cta: 'Explore Ceramic Coating',
      link: '/ceramic-coating'
    },
    {
      id: 'paint-correction',
      title: 'Paint Correction',
      description: 'Reduce swirl marks, oxidation, haze, and light defects while restoring clarity and depth to the finish. The correction level is chosen after inspecting the paint.',
      cta: 'Request a Paint Inspection',
      link: '/paint-correction'
    },
    {
      id: 'premium-detail',
      title: 'Premium Interior & Exterior Detailing',
      description: 'A thorough reset for vehicles that need more than maintenance. Services can include deep interior cleaning, steam treatment, shampooing, and durable exterior protection.',
      cta: 'View Detailing Options',
      link: '/services'
    },
    {
      id: 'maintenance',
      title: 'Maintenance Detailing',
      description: 'Preserve a clean, well-kept finish with deionized water, careful contact methods, fresh exterior towels, forced-air drying, and consistent interior upkeep.',
      cta: 'View Maintenance Care',
      link: '/services#maintenance'
    }
  ];

  // Service Area Cities
  const serviceCities = [
    'Simi Valley',
    'Thousand Oaks',
    'Westlake Village',
    'Agoura Hills',
    'Calabasas',
    'Moorpark',
    'West Hills',
    'Woodland Hills',
    'Encino',
    'Santa Monica',
    'Beverly Hills',
    'Malibu'
  ];

  return (
    <div className="min-h-screen bg-[#111216]">
      <SEO 
        title="Premium Auto Detailing Simi Valley | Detailing Disciples"
        description="Owner-operated auto detailing, paint correction and ceramic coating in Simi Valley, Ventura County and select Los Angeles areas. Request a quote."
        canonical="https://thedetailingdisciples.com"
      />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_DATA.heroImage}
            alt="Premium auto detailing Simi Valley"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#111216]/90 via-[#111216]/85 to-[#111216]"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 text-center pt-20 pb-16">
          <div className="max-w-5xl mx-auto space-y-6">
            {/* Eyebrow */}
            <p className="text-[#B8860B] text-sm md:text-base font-semibold tracking-wide uppercase">
              Owner-Operated · Mobile & Appointment-Only Garage Detailing
            </p>

            {/* H1 - SEO Optimized */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight px-4">
              Premium Auto Detailing, Paint Correction & Ceramic Coating
              <br className="hidden md:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#B8860B] to-[#C8960B]">
                {' '}in Simi Valley
              </span>
            </h1>

            {/* Supporting Paragraph */}
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto px-4 leading-relaxed">
              Careful, low-contact vehicle care for owners who expect more than a quick wash. Detailing Disciples serves Ventura County and select Los Angeles communities with premium mobile service and appointment-only garage detailing.
            </p>

            {/* Primary CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6 px-4">
              <Link to="/contact" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-gradient-to-r from-[#B8860B] to-[#C8960B] hover:from-[#C8960B] hover:to-[#B8860B] text-[#111216] font-bold text-lg px-8 py-7 shadow-2xl shadow-[#B8860B]/40 hover:scale-105 transition-all duration-300"
                >
                  <Calendar className="w-6 h-6 mr-2" />
                  Request a Personalized Quote
                </Button>
              </Link>
              <a href={`tel:${HERO_DATA.phone.replace(/[^0-9]/g, '')}`} className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-white hover:border-[#B8860B] text-white hover:bg-[#B8860B]/20 font-semibold text-lg px-8 py-7 backdrop-blur-md transition-all duration-300"
                >
                  <Phone className="w-6 h-6 mr-2" />
                  Call or Text {HERO_DATA.phone}
                </Button>
              </a>
            </div>

            {/* Tertiary CTA */}
            <div className="pt-2">
              <Link to="/results" className="text-[#B8860B] hover:text-[#C8960B] font-medium inline-flex items-center gap-2 transition-colors">
                See Real Results
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Paint-Safe Proof Bar */}
      <section className="py-6 bg-gradient-to-r from-[#1A1A1F] to-[#111216] border-y border-white/10">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 max-w-6xl mx-auto text-center">
            {proofPoints.map((point) => (
              <div key={point} className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-[#B8860B] flex-shrink-0" />
                <span className="text-white text-sm md:text-base font-medium">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem/Value Introduction */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#111216] to-[#1A1A1F]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              Your Vehicle Deserves a Process Built Around Its Paint
            </h2>
            <div className="space-y-4 text-lg text-gray-300 leading-relaxed">
              <p>
                Fast washes and reused towels can leave behind the very swirls, haze, and water spots you are paying to remove. Detailing Disciples takes a more deliberate approach: careful inspection, paint-safe washing, controlled contact, and protection selected around your vehicle and how you use it.
              </p>
              <p>
                Every appointment is handled with the same goal—return a cleaner, sharper, easier-to-maintain vehicle without careless shortcuts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Signature Services */}
      <section className="py-16 md:py-24 bg-[#1A1A1F]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Premium <span className="text-[#B8860B]">Services</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              Choose the result you want. We will recommend the right level of care based on the vehicle's size, condition, use, and the areas you want to improve.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-8">
            {signatureServices.map((service) => (
              <div
                key={service.id}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-[#B8860B]/50 hover:bg-white/10 transition-all duration-300 group"
              >
                <h3 className="text-white font-bold text-2xl mb-4">{service.title}</h3>
                <p className="text-gray-400 mb-6 leading-relaxed">{service.description}</p>
                <Link to={service.link}>
                  <Button
                    variant="outline"
                    className="w-full border-2 border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-[#111216] font-semibold group-hover:scale-105 transition-all"
                  >
                    {service.cta}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real Results Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Real Vehicles. Real Paint. <span className="text-[#B8860B]">Real Results.</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              See actual Detailing Disciples work—from swirl removal and ceramic coating to interior restoration and maintenance care. Every featured project identifies the vehicle, service, starting condition, and result.
            </p>
          </div>

          <div className="text-center">
            <Link to="/results">
              <Button
                size="lg"
                className="bg-[#B8860B] hover:bg-[#C8960B] text-[#111216] font-bold"
              >
                See Before & After Results
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Owner-Operated Differentiators */}
      <section className="py-16 md:py-24 bg-[#1A1A1F]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Care You Can See in <span className="text-[#B8860B]">the Process</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {differentiators.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.id}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-[#B8860B]/30 transition-all"
                >
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#B8860B]/20 to-[#C8960B]/20 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-[#B8860B]" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2 text-center">{item.title}</h3>
                  <p className="text-gray-400 text-sm text-center leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* The Method Preview */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              The Detailing Disciples <span className="text-[#B8860B]">Method</span>
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              Premium results come from careful decisions at every stage. Our paint-safe method is designed to reduce unnecessary contact, identify the right level of correction, and protect the finish after the work is complete.
            </p>
            <Link to="/method">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-[#111216] font-semibold mt-4"
              >
                See Our 6-Step Process
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Verified Reviews */}
      <section className="py-16 md:py-24 bg-[#1A1A1F]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              What Our <span className="text-[#B8860B]">Clients Say</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Real reviews from real clients across Ventura County & Los Angeles
            </p>
            <div className="flex items-center justify-center gap-2 mt-4">
              {[...Array(5)].map((_, i) => (
                <Star key={`home-review-star-${i}`} className="w-6 h-6 fill-[#B8860B] text-[#B8860B]" />
              ))}
              <span className="text-white font-bold text-xl ml-2">5.0</span>
              <span className="text-gray-400 ml-1">({HERO_DATA.reviewCount} reviews on Yelp)</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-8">
            {REVIEWS_DATA.slice(0, 3).map((review) => (
              <div
                key={review.id}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
              >
                <div className="flex items-center mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={`${review.id}-rating-star-${i}`} className="w-5 h-5 fill-[#B8860B] text-[#B8860B]" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  &ldquo;{review.text}&rdquo;
                </p>
                <div className="border-t border-white/10 pt-4">
                  <p className="text-white font-semibold">{review.name}</p>
                  <p className="text-[#B8860B] text-sm">{review.location}</p>
                  <p className="text-gray-500 text-xs mt-1">{review.service}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center space-y-4">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://www.yelp.com/biz/detailing-disciples-simi-valley-2"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="outline"
                  className="border-2 border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-[#111216]"
                >
                  Read All Yelp Reviews
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a
                href="https://g.page/r/CbkD6ZBDY-FpEBM/review"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="outline"
                  className="border-2 border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-[#111216]"
                >
                  Read Google Reviews
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Based in <span className="text-[#B8860B]">Simi Valley</span>. Serving Ventura County and Select Los Angeles Communities.
            </h2>
            <p className="text-gray-300 text-lg max-w-4xl mx-auto leading-relaxed">
              Mobile and appointment-only service is available in Simi Valley, Thousand Oaks, Westlake Village, Agoura Hills, Calabasas, Moorpark, West Hills, Santa Monica, Beverly Hills, Malibu, and nearby areas. Travel availability varies by service, location, and schedule.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {serviceCities.map((city) => (
                <div
                  key={city}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3 flex items-center space-x-2 hover:border-[#B8860B]/50 transition-all"
                >
                  <MapPin className="w-4 h-4 text-[#B8860B] flex-shrink-0" />
                  <span className="text-white text-sm font-medium">{city}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-8">
            <Link to="/contact">
              <Button
                variant="outline"
                className="border-2 border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-[#111216]"
              >
                Check Availability
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-[#B8860B] to-[#C8960B] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23000000\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#111216] mb-4 px-4">
            Tell Us What You Want to Improve
          </h2>
          <p className="text-[#111216]/90 text-lg md:text-xl mb-8 max-w-2xl mx-auto px-4">
            Share your vehicle, location, and main concerns. You will receive a recommendation based on the condition of the vehicle and the result you want—not a generic upsell.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 px-4">
            <Link to="/contact" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-[#111216] hover:bg-[#1A1A1F] text-white font-bold text-lg px-8 py-6 shadow-2xl hover:scale-105 transition-all duration-300"
              >
                <Calendar className="w-6 h-6 mr-2" />
                Request a Personalized Quote
              </Button>
            </Link>
            <a href={`tel:${HERO_DATA.phone.replace(/[^0-9]/g, '')}`} className="w-full sm:w-auto">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-2 border-[#111216] text-[#111216] hover:bg-[#111216] hover:text-white font-semibold text-lg px-8 py-6 transition-all duration-300"
              >
                <Phone className="w-6 h-6 mr-2" />
                Call or Text {HERO_DATA.phone}
              </Button>
            </a>
          </div>
          <p className="text-[#111216]/70 text-sm mt-6">
            <Shield className="w-4 h-4 inline mr-1" />
            100% Satisfaction Guaranteed • Same-Day Service Available
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
